import { ref, computed, type Ref, type ComputedRef } from "vue";
import {
  GetShopDocument,
  type GetShopQuery,
  type GetShopQueryVariables,
} from "@/generated/graphql";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { getShopList } from "@/common/graphql/shop";
import type { ShopItem } from "../types";

/**
 * 仮会員QRコード画面の初期ロード・店舗選択
 * 店舗権限では自店舗情報を取得し、本部権限では店舗一覧を取得する。
 * HQ権限時の店舗セレクト変更ハンドラも提供する。
 *
 * @param deps.isStore - 店舗権限かどうか
 * @param deps.isHQ - 本部権限かどうか
 * @param deps.showError - エラーダイアログ表示関数
 * @param deps.resolveAndRenderUrl - URL生成→QR描画を実行する関数
 * @param deps.clearSelection - 店舗セレクタクリア時にURLをリセットする関数
 *
 * @returns selectedShopCode - 選択中の店舗コード
 * @returns currentShopName - 表示中の店舗名
 * @returns currentInternalId - m_shop.internal_id（URLのstoreIdに使用）
 * @returns tmpMemberEnabled - 仮会員登録機能の有効/無効
 * @returns tmpMemberEnabledChecked - 店舗情報取得完了判定（店舗権限初回ロードおよびHQ店舗選択時）
 * @returns shopListItems - HQ向け店舗一覧
 * @returns selectableShops - セレクトボックス選択肢
 * @returns runInitialLoad - 初期ロード実行（onMounted から呼び出し）
 * @returns onSelectShop - HQ店舗セレクト変更時ハンドラ
 */
export const useTmpMemberQrCodeInit = (deps: {
  isStore: ComputedRef<boolean>;
  isHQ: ComputedRef<boolean>;
  showError: (msg: string) => void;
  resolveAndRenderUrl: () => Promise<void>;
  clearSelection: () => void;
}) => {
  const selectedShopCode = ref<string | null>(null);
  const currentShopName = ref("");
  const currentInternalId = ref("");
  const tmpMemberEnabled = ref(false);
  const tmpMemberEnabledChecked = ref(false);
  // 店舗情報取得中フラグ（初期値 false により初回描画時のちらつきを防止）
  // ちらつき防止のため、一定時間（LOADING_DELAY_MS）経過後にのみ表示する
  const isLoading = ref(false);
  const LOADING_DELAY_MS = 300;
  let loadingTimer: ReturnType<typeof setTimeout> | null = null;

  // HQ向け店舗一覧
  const shopListItems = ref<(ShopItem | null | undefined)[] | null | undefined>([]);

  /**
   * セレクトボックスの選択肢
   */
  const selectableShops = computed(() => {
    return (shopListItems.value ?? []).map((s: ShopItem | null | undefined) => ({
      shop_code: s?.shop_code,
      label: `${s?.name}`,
    }));
  });

  /**
   * 店舗詳細を取得して状態を更新→URL生成→QR描画
   * 呼び出し元（onSelectShop）で tmpMemberEnabledChecked=false にリセットしておくこと
   */
  const fetchShopAndRender = async (shopCode: string): Promise<void> => {
    try {
      const client = await generateApolloClient();
      const res = await client.query<GetShopQuery, GetShopQueryVariables>({
        query: GetShopDocument,
        variables: {
          customer_code: localStorageGetCustomerCode(),
          chain_code: localStorageGetChainCode(),
          shop_code: shopCode,
        },
        fetchPolicy: "no-cache",
      });

      const shop = res.data.getShop;
      if (!shop) return;

      currentShopName.value = shop.name;
      currentInternalId.value = shop.internal_id;
      tmpMemberEnabled.value = !!shop.tmp_member_registration_flg;
      tmpMemberEnabledChecked.value = true;

      await deps.resolveAndRenderUrl();
    } catch (e: unknown) {
      deps.showError("店舗情報の取得に失敗しました。");
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * 初期ロード実行（onMounted から呼び出し）
   * 店舗権限：自店舗の情報を取得→URL生成→QR描画
   * 本部権限：自チェーンの店舗一覧を取得
   */
  const runInitialLoad = async (): Promise<void> => {
    try {
      const client = await generateApolloClient();

      if (deps.isStore.value) {
        // 店舗権限：自店舗の情報を取得
        // ちらつき防止：LOADING_DELAY_MS 以内に完了すればローディング表示しない
        loadingTimer = setTimeout(() => {
          isLoading.value = true;
        }, LOADING_DELAY_MS);
        try {
          const res = await client.query<GetShopQuery, GetShopQueryVariables>({
            query: GetShopDocument,
            variables: {
              customer_code: localStorageGetCustomerCode(),
              chain_code: localStorageGetChainCode(),
              shop_code: localStorageGetShopCode(),
            },
            fetchPolicy: "network-only",
          });

          const shop = res.data.getShop;
          if (shop) {
            selectedShopCode.value = shop.shop_code;
            currentShopName.value = shop.name;
            currentInternalId.value = shop.internal_id;
            tmpMemberEnabled.value = !!shop.tmp_member_registration_flg;
            tmpMemberEnabledChecked.value = true;
            // 店舗情報取得後にURL生成→QR描画
            await deps.resolveAndRenderUrl();
          }
        } finally {
          if (loadingTimer) {
            clearTimeout(loadingTimer);
            loadingTimer = null;
          }
          isLoading.value = false;
        }
      } else if (deps.isHQ.value) {
        // 本部権限：自チェーンの店舗一覧を初期ロード
        shopListItems.value = await getShopList({
          chain_code: localStorageGetChainCode(),
          customer_code: localStorageGetCustomerCode(),
          limit: 5000,
          offset: 0,
        });
      }
    } catch (e: unknown) {
      deps.showError("画面表示に失敗しました。");
    }
  };

  /**
   * HQ店舗セレクト変更時ハンドラ
   * 選択された店舗の詳細を取得→URL生成→QR描画
   * クリア時は状態を初期化してQRコード表示セクションを非表示にする
   */
  const onSelectShop = async (): Promise<void> => {
    if (!selectedShopCode.value) {
      // 店舗セレクタクリア時：状態を初期化
      currentShopName.value = "";
      currentInternalId.value = "";
      tmpMemberEnabled.value = false;
      tmpMemberEnabledChecked.value = false;
      deps.clearSelection();
      return;
    }
    // 店舗情報取得前に確認済みフラグをリセットし、「機能無効」警告のちらつきを防止
    tmpMemberEnabledChecked.value = false;
    isLoading.value = true;
    await fetchShopAndRender(selectedShopCode.value);
  };

  return {
    selectedShopCode,
    currentShopName,
    currentInternalId,
    tmpMemberEnabled,
    tmpMemberEnabledChecked,
    isLoading,
    shopListItems,
    selectableShops,
    runInitialLoad,
    onSelectShop,
  };
};
